import React, { useState } from 'react';
import { Shield, Users, Lock, Eye, FileText, Mail, AlertTriangle, Calendar } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const navigationItems = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'data-collection', label: 'Data Collection', icon: FileText },
    { id: 'data-use', label: 'Data Usage', icon: Users },
    { id: 'child-protection', label: 'Child Protection', icon: Shield },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'contact', label: 'Contact', icon: Mail }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-lg text-gray-600 mb-2">GFELTP Nexus</p>
            <p className="text-sm text-gray-500 flex items-center justify-center">
              <Calendar className="h-4 w-4 mr-2" />
              Last updated: May, 2025
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 flex gap-8">
        {/* Navigation Sidebar */}
        <div className="w-64 flex-shrink-0">
          <div className="sticky top-8">
            <nav className="bg-white rounded-lg shadow-sm border p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Navigation</h3>
              <ul className="space-y-2">
                {navigationItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full text-left px-3 py-2 rounded-md flex items-center transition-colors ${
                          activeSection === item.id 
                            ? 'bg-blue-100 text-blue-700 font-medium' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <IconComponent className="h-4 w-4 mr-2" />
                        {item.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-sm border p-8 space-y-12">
            
            {/* Overview Section */}
            <section id="overview">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Eye className="h-8 w-8 mr-3 text-blue-600" />
                Privacy Policy Overview
              </h2>
              
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-4">
                  This Privacy Policy describes our policies and procedures on the collection, use and disclosure 
                  of your information when you use the GFELTP Nexus service. It explains your privacy rights and 
                  how the law protects you.
                </p>
                
                <p className="text-gray-700 mb-6">
                  We use your personal data to provide and improve the service. By using the service, you agree 
                  to the collection and use of information in accordance with this Privacy Policy.
                </p>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                  <h4 className="font-semibold text-blue-800 mb-2">About GFELTP Nexus</h4>
                  <p className="text-blue-700">
                    <strong>Company:</strong> Ghana FELTP, University of Ghana, School of Public Health<br/>
                    <strong>Country:</strong> Ghana<br/>
                    <strong>Application:</strong> GFELTP Nexus - Software program for field epidemiology training
                  </p>
                </div>
              </div>
            </section>

            {/* Data Collection Section */}
            <section id="data-collection">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <FileText className="h-8 w-8 mr-3 text-green-600" />
                Data Collection
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Personal Data We Collect</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-800 mb-2">Contact Information</h4>
                      <ul className="text-gray-600 text-sm space-y-1">
                        <li>• Email address</li>
                        <li>• First name and last name</li>
                        <li>• Phone number</li>
                        <li>• Address details</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-800 mb-2">Device Information</h4>
                      <ul className="text-gray-600 text-sm space-y-1">
                        <li>• IP address</li>
                        <li>• Browser type and version</li>
                        <li>• Device identifiers</li>
                        <li>• Operating system</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Usage Data</h3>
                  <p className="text-gray-700 mb-3">
                    Usage data is collected automatically and may include information such as pages visited, 
                    time spent on pages, and diagnostic data to improve our service.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Application Permissions</h3>
                  <p className="text-gray-700 mb-3">
                    With your prior permission, we may collect:
                  </p>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Location information for service features</li>
                    <li>• Photos and camera access for application functionality</li>
                  </ul>
                  <p className="text-sm text-gray-600 mt-2">
                    You can enable or disable these permissions through your device settings at any time.
                  </p>
                </div>
              </div>
            </section>

            {/* Data Usage Section */}
            <section id="data-use">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Users className="h-8 w-8 mr-3 text-purple-600" />
                How We Use Your Data
              </h2>
              
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-800">Service Provision</h4>
                    <p className="text-gray-600 text-sm">Maintain and monitor service usage</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-800">Account Management</h4>
                    <p className="text-gray-600 text-sm">Manage registration and user access</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-800">Communication</h4>
                    <p className="text-gray-600 text-sm">Updates, notifications, and support</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-800">Improvement</h4>
                    <p className="text-gray-600 text-sm">Analytics and service enhancement</p>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                  <h4 className="font-semibold text-yellow-800 mb-2">Data Sharing</h4>
                  <p className="text-yellow-700 text-sm mb-2">We may share your information with:</p>
                  <ul className="text-yellow-700 text-sm space-y-1">
                    <li>• Service providers for monitoring and analysis</li>
                    <li>• Business partners for relevant services</li>
                    <li>• Affiliates (with same privacy standards)</li>
                    <li>• During business transfers or legal requirements</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Child Protection Section */}
            <section id="child-protection">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Shield className="h-8 w-8 mr-3 text-red-600" />
                Child Protection Policy
              </h2>
              
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-6">
                <div className="flex items-start">
                  <AlertTriangle className="h-6 w-6 text-red-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-red-800 mb-3">Zero Tolerance Policy</h3>
                    <p className="text-red-700 font-medium">
                      GFELTP Nexus has a zero-tolerance policy regarding child sexual abuse and exploitation. 
                      We are committed to creating and maintaining a safe environment for all users.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Age Restrictions</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 mb-2">
                      <strong>Our service does not address anyone under the age of 13.</strong> We do not knowingly 
                      collect personally identifiable information from children under 13.
                    </p>
                    <p className="text-gray-700">
                      If you are a parent or guardian and believe your child has provided us with personal data, 
                      please contact us immediately. We will take steps to remove such information from our servers.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Protective Measures</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2">Content Monitoring</h4>
                      <p className="text-blue-700 text-sm">
                        We actively monitor user-generated content and interactions to identify and prevent 
                        inappropriate behavior or content that could harm minors.
                      </p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-medium text-green-800 mb-2">Reporting System</h4>
                      <p className="text-green-700 text-sm">
                        Users can report suspicious activity or inappropriate content through our secure 
                        reporting system, which is reviewed immediately by our safety team.
                      </p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-medium text-purple-800 mb-2">Account Verification</h4>
                      <p className="text-purple-700 text-sm">
                        We implement verification procedures to ensure users meet age requirements and 
                        maintain legitimate educational purposes.
                      </p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-800 mb-2">Law Enforcement</h4>
                      <p className="text-orange-700 text-sm">
                        We cooperate fully with law enforcement agencies and report suspected illegal 
                        activity involving minors to appropriate authorities.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2">Reporting Concerns</h4>
                  <p className="text-red-700 mb-2">
                    If you suspect any form of child abuse or exploitation on our platform, please report it immediately:
                  </p>
                  <ul className="text-red-700 text-sm space-y-1">
                    <li>• Email: <a href="mailto:gfeltp@gmail.com" className="underline font-medium">gfeltp@gmail.com</a> (mark as URGENT - Child Safety)</li>
                    <li>• All reports are investigated within 24 hours</li>
                    <li>• We maintain strict confidentiality for reporters</li>
                    <li>• Immediate action is taken to protect potential victims</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Security Section */}
            <section id="security">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Lock className="h-8 w-8 mr-3 text-indigo-600" />
                Data Security & Rights
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Security Measures</h3>
                  <p className="text-gray-700 mb-4">
                    The security of your personal data is important to us. While no method of transmission over 
                    the Internet is 100% secure, we strive to use commercially acceptable means to protect your data.
                  </p>
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <p className="text-indigo-700 text-sm">
                      Your data is processed at our operating offices and secure servers. International transfers 
                      include adequate security controls and comply with applicable data protection laws.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Your Rights</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border border-gray-200 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-800 mb-2">Access & Correction</h4>
                      <p className="text-gray-600 text-sm">
                        You can access and update your personal information through your account settings.
                      </p>
                    </div>
                    <div className="border border-gray-200 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-800 mb-2">Data Deletion</h4>
                      <p className="text-gray-600 text-sm">
                        You have the right to request deletion of your personal data, subject to legal obligations.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Data Retention</h3>
                  <p className="text-gray-700">
                    We retain personal data only as long as necessary for the purposes outlined in this policy, 
                    legal compliance, dispute resolution, and policy enforcement.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section id="contact">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Mail className="h-8 w-8 mr-3 text-teal-600" />
                Contact Information
              </h2>
              
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-teal-800 mb-4">Get in Touch</h3>
                <p className="text-teal-700 mb-4">
                  If you have any questions about this Privacy Policy or our child protection measures, 
                  you can contact us:
                </p>
                <div className="space-y-2">
                  <p className="text-teal-700">
                    <strong>Email:</strong> <a href="mailto:gfeltp@gmail.com" className="underline">gfeltp@gmail.com</a>
                  </p>
                  <p className="text-teal-700">
                    <strong>Organization:</strong> Ghana FELTP, University of Ghana, School of Public Health
                  </p>
                  <p className="text-teal-700">
                    <strong>Country:</strong> Ghana
                  </p>
                </div>
                
                <div className="mt-4 p-3 bg-white border border-teal-300 rounded">
                  <p className="text-teal-800 text-sm font-medium">Policy Updates</p>
                  <p className="text-teal-700 text-sm">
                    We may update this Privacy Policy periodically. Changes will be posted on this page 
                    with an updated revision date. Significant changes will be communicated via email.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;